import { consumeAgUiStream } from '@/lib/agui/stream';
import {
  reduceAgentLiveAgUiEvent,
  type AgentLiveAgUiState,
  type AgentLiveSlug,
} from '@/lib/agui/agentLive';

import type { AgentRunNormalized, AgentRunResult, AgentRunState } from './types';

export type AgentLiveRunRequest = {
  agent: AgentLiveSlug;
  clientId?: string;
  input: string;
  language?: 'en' | 'ar';
  previousContent?: string;
  sourceConversationId?: string;
  requestId?: string;
  meta?: Record<string, unknown>;
  work?: Record<string, unknown>;
};

export type AgentLiveRunResult = AgentRunResult & {
  /**
   * True when the dedicated live route is not deployed/reachable as a
   * route (404/405/501), letting callers fall back to another transport.
   */
  transportUnavailable?: boolean;
  agentLiveState?: AgentLiveAgUiState | null;
};

function normalizeTerminalState(state: AgentLiveAgUiState | null): AgentLiveRunResult {
  if (!state) {
    return { ok: false, error: 'agent live stream ended without terminal state' };
  }

  if (state.status === 'failed') {
    return {
      ok: false,
      error: state.error || state.summary || 'agent live run failed',
      agentLiveState: state,
    };
  }

  const rawState: AgentRunState = {
    runId: state.runId,
    threadId: state.threadId,
    clientId: state.clientId,
    task: state.agentId,
    draft: state.draftContent,
    meta: {
      agentLive: {
        agentId: state.agentId,
        requestId: state.requestId,
        stage: state.stage,
        status: state.status,
        approvalPending: state.approvalPending,
        ...(state.approvalContext ? { approvalContext: state.approvalContext } : {}),
        ...(Object.keys(state.extras || {}).length ? { extras: state.extras } : {}),
      },
    },
  };

  const normalized: AgentRunNormalized = {
    runId: state.runId,
    task: state.agentId,
    route: state.agentId,
    content: state.draftContent || state.summary || '',
    rawState,
  };

  return {
    ok: true,
    data: {
      state: rawState,
      normalized,
    },
    agentLiveState: state,
  };
}

/**
 * Promise-style client for the dedicated per-agent AG-UI live routes
 * (`/api/agents/<agent>/run/live`). Mirrors runRuntimeToolViaSSE so the
 * result plugs into the existing chat quick-action flow unchanged.
 */
export async function runAgentLiveViaSSE(req: AgentLiveRunRequest): Promise<AgentLiveRunResult> {
  let authHeader: Record<string, string> = {};
  try {
    const { auth } = await import('@/lib/firebase');
    const maybeUser = (auth as { currentUser?: { getIdToken: () => Promise<string> } | null })?.currentUser;
    const token = maybeUser ? await maybeUser.getIdToken() : '';
    if (token) authHeader = { Authorization: `Bearer ${token}` };
  } catch {
    authHeader = {};
  }

  const response = await fetch(`/api/agents/${req.agent}/run/live`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...authHeader,
    },
    body: JSON.stringify({
      clientId: req.clientId || 'default',
      input: req.input,
      ...(req.language ? { language: req.language } : {}),
      ...(req.previousContent ? { previousContent: req.previousContent } : {}),
      ...(req.sourceConversationId ? { sourceConversationId: req.sourceConversationId } : {}),
      ...(req.requestId ? { requestId: req.requestId } : {}),
      ...(req.meta ? { meta: req.meta } : {}),
      ...(req.work ? { work: req.work } : {}),
    }),
  });

  if (!response.ok || !response.body) {
    const errorText = await response.text().catch(() => `/api/agents/${req.agent}/run/live ${response.status}`);
    return {
      ok: false,
      error: errorText || `/api/agents/${req.agent}/run/live ${response.status}`,
      transportUnavailable: response.status === 404 || response.status === 405 || response.status === 501,
    };
  }

  let latestState: AgentLiveAgUiState | null = null;
  const { terminalType } = await consumeAgUiStream<AgentLiveAgUiState>({
    stream: response.body,
    onEvent: (event) => {
      latestState = reduceAgentLiveAgUiEvent(latestState, event);
    },
  }).catch((error) => {
    return {
      terminalType: (error as Error)?.message ? 'error' as const : null,
    };
  });

  if (terminalType === 'error' && (!latestState || (latestState as AgentLiveAgUiState).status !== 'completed')) {
    const failedState = latestState as AgentLiveAgUiState | null;
    return {
      ok: false,
      error: failedState?.error || failedState?.summary || 'agent live run failed',
      agentLiveState: failedState,
    };
  }

  return normalizeTerminalState(latestState);
}
